import React, { useCallback, useEffect, useState } from 'react';
import AuthenticationContext from '../contexts/AuthenticationContext';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import BrandedBootLoader from '../components/BrandedBootLoader';

const AuthenticationProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const [uid, setUid] = useState<string | undefined>();
  const [email, setEmail] = useState<string | undefined>();
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [isWallboardUser, setIsWallBoardUser] = useState<boolean>();
  const [wallBoardAccountId, setWallBoardAccountId] = useState<string>();
  const [isRenderUser, setIsRenderUser] = useState<boolean>();
  const [renderRunId, setRenderRunId] = useState<string>();
  const [renderAccountId, setRenderAccountId] = useState<string>();
  const [renderReportId, setRenderReportId] = useState<string>();
  const [renderTokenJti, setRenderTokenJti] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isClaimsEvaluated, setIsClaimsEvaluated] = useState<boolean>(false);

  const onLoggedOut = useCallback(() => {
    setUid(undefined);
    setEmail(undefined);
    setAuthenticated(false);
    setIsSuperAdmin(false);
    setIsWallBoardUser(false);
    setWallBoardAccountId(undefined);
    setIsRenderUser(false);
    setRenderRunId(undefined);
    setRenderAccountId(undefined);
    setRenderReportId(undefined);
    setRenderTokenJti(undefined);
  }, []);

  useEffect(() => {
    let isActive = true;
    firebase.auth().onAuthStateChanged((updatedSession) => {
      if (!isActive) {
        return;
      }

      setAuthenticated(!!updatedSession);
      if (updatedSession) {
        setUid(updatedSession.uid);
        setEmail(updatedSession.email ? updatedSession.email : 'Not available');
        updatedSession.getIdTokenResult().then(({ claims }) => {
          if (isActive) {
            setIsSuperAdmin(claims.isSuperAdmin);
            setIsWallBoardUser(!!claims.wallboardId);
            setWallBoardAccountId(
              Array.isArray(claims.accountId)
                ? claims.accountId[0]
                : claims.accountId,
            );
            const accountClaim = Array.isArray(claims.accountId)
              ? claims.accountId[0]
              : claims.accountId;
            setIsRenderUser(!!claims.renderRun);
            setRenderRunId(claims.renderRun as string | undefined);
            setRenderAccountId(
              claims.renderRun
                ? (accountClaim as string | undefined)
                : undefined,
            );
            setRenderReportId(claims.reportId as string | undefined);
            setRenderTokenJti(claims.renderJti as string | undefined);
            setIsClaimsEvaluated(true);
          }
        });
      } else {
        onLoggedOut();
      }
      setIsLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [onLoggedOut]);

  // Render the same branded loader as FirebaseGate and SplashScreen so cold
  // boot reads as one continuous screen — previously this bare, re-centred
  // spinner (no logo) caused a visible "jump" between the two branded loaders.
  if (isLoading || (authenticated && (!uid || !email || !isClaimsEvaluated))) {
    return <BrandedBootLoader />;
  }

  return (
    <AuthenticationContext.Provider
      value={{
        authenticated,
        isLoading,
        uid,
        isSuperAdmin,
        isWallboardUser,
        wallBoardAccountId,
        isRenderUser,
        renderRunId,
        renderAccountId,
        renderReportId,
        renderTokenJti,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export default AuthenticationProvider;
